import {
  Activity as ActivityIcon,
  Archive,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronDown,
  Clock3,
  Dumbbell,
  Edit3,
  Flame,
  Layers3,
  LogOut,
  Moon,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Sparkles,
  Square,
  Sun,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  UserRound,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import {
  ApiError,
  api,
  clearToken,
  getStoredToken,
  googleClientId,
  storeToken,
} from "./api";
import type { Activity, AuthResult, Dashboard, Session, User } from "./types";

type View = "today" | "insights" | "activities" | "account";
type InsightPeriod = "7d" | "30d" | "lifetime";

const NAVIGATION: Array<{
  id: View;
  label: string;
  icon: typeof Sun;
}> = [
  { id: "today", label: "Today", icon: Sun },
  { id: "insights", label: "Insights", icon: BarChart3 },
  { id: "activities", label: "Activities", icon: Layers3 },
  { id: "account", label: "Account", icon: UserRound },
];

const ACTIVITY_PRESETS = [
  { name: "Japanese", category: "Learning", color: "#6c63e8", icon: "language" },
  { name: "Workout", category: "Health", color: "#20a87b", icon: "dumbbell" },
  { name: "Study", category: "Subjects", color: "#d9932e", icon: "book-open" },
];

function formatDuration(seconds: number, compact = false): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3_600);
  const minutes = Math.floor((safeSeconds % 3_600) / 60);
  const remainingSeconds = safeSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return compact ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}

function formatClock(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  return [
    Math.floor(safeSeconds / 3_600),
    Math.floor((safeSeconds % 3_600) / 60),
    safeSeconds % 60,
  ]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

function formatDelta(seconds: number): string {
  if (seconds === 0) return "Same as the previous period";
  return `${formatDuration(Math.abs(seconds), true)} ${seconds > 0 ? "more" : "less"}`;
}

function formatLocalDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

function formatSessionTime(session: Session): string {
  const date = new Date(session.startedAt);
  const end = session.endedAt ? new Date(session.endedAt) : null;
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
  const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
  const endTime = end
    ? new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(end)
    : "now";
  return `${day} · ${time}–${endTime}`;
}

function activityGlyph(activity: Pick<Activity, "icon" | "name"> | { icon?: string | null; name: string }) {
  const icon = activity.icon ?? "clock";
  if (icon === "dumbbell") return <Dumbbell aria-hidden="true" />;
  if (icon === "book-open") return <BookOpen aria-hidden="true" />;
  if (icon === "language") return <span aria-hidden="true">あ</span>;
  return <ActivityIcon aria-hidden="true" />;
}

function initials(name: string): string {
  return name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "TT";
}

function localInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function LoadingScreen() {
  return (
    <main className="loading-screen" aria-label="Loading HourSpeed">
      <span className="brand-mark"><Timer aria-hidden="true" /></span>
      <p>Making your time visible…</p>
    </main>
  );
}

function GoogleButton({
  label,
  onCredential,
  disabled,
}: {
  label: "signin_with" | "signup_with";
  onCredential: (credential: string) => void;
  disabled: boolean;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const clientId = googleClientId();

  useEffect(() => {
    if (clientId === "" || disabled) return;
    let active = true;
    const initialize = () => {
      if (!active || !window.google || !buttonRef.current) return;
      buttonRef.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredential(response.credential),
        ux_mode: "popup",
        use_fedcm_for_prompt: true,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: label,
        shape: "rectangular",
        width: Math.min(360, buttonRef.current.clientWidth),
        logo_alignment: "left",
      });
    };

    const existing = document.querySelector<HTMLScriptElement>("script[data-google-identity]");
    if (window.google) {
      initialize();
    } else if (existing) {
      existing.addEventListener("load", initialize, { once: true });
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = "true";
      script.addEventListener("load", initialize, { once: true });
      document.head.appendChild(script);
    }
    return () => {
      active = false;
      existing?.removeEventListener("load", initialize);
    };
  }, [clientId, disabled, label, onCredential]);

  if (clientId === "") {
    return (
      <button className="google-placeholder" type="button" disabled>
        <span className="google-g">G</span> Continue with Google
      </button>
    );
  }
  return <div className="google-button-slot" ref={buttonRef} aria-busy={disabled} />;
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: (result: AuthResult) => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

  const complete = useCallback(
    (result: AuthResult) => {
      storeToken(result.accessToken);
      onAuthenticated(result);
    },
    [onAuthenticated],
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result =
        mode === "login"
          ? await api.login(email, password)
          : await api.register(email, password, displayName, timezone);
      complete(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Authentication failed.");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = useCallback(
    async (credential: string) => {
      setBusy(true);
      setError(null);
      try {
        complete(await api.google(credential, timezone));
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Google sign-in failed.");
      } finally {
        setBusy(false);
      }
    },
    [complete, timezone],
  );

  return (
    <main className="auth-layout">
      <section className="auth-story" aria-labelledby="auth-story-title">
        <div className="auth-brand"><span className="brand-mark"><Timer aria-hidden="true" /></span><span>HourSpeed</span></div>
        <div className="auth-story-copy">
          <span className="eyebrow"><Sparkles aria-hidden="true" /> Your effort adds up</span>
          <h1 id="auth-story-title">Make the time you invest in yourself visible.</h1>
          <p>Track Japanese, workouts, subjects, work, and anything else that matters. See today, compare periods, and watch your lifetime total grow.</p>
        </div>
        <div className="auth-preview" aria-label="Example progress">
          <div><span>Japanese</span><strong>126h 18m</strong></div>
          <div><span>This week</span><strong>5h 14m</strong></div>
          <div className="auth-preview-track"><span style={{ width: "76%" }} /></div>
          <p><TrendingUp aria-hidden="true" /> 42 minutes more than last week</p>
        </div>
      </section>

      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="mobile-auth-brand"><span className="brand-mark"><Timer aria-hidden="true" /></span><span>HourSpeed</span></div>
        <div className="auth-card">
          <p className="auth-kicker">{mode === "login" ? "Welcome back" : "Start your lifetime total"}</p>
          <h2 id="auth-title">{mode === "login" ? "Sign in to HourSpeed" : "Create your account"}</h2>
          <p>{mode === "login" ? "Continue where you left off." : "Your first activity takes less than a minute."}</p>

          <GoogleButton label={mode === "login" ? "signin_with" : "signup_with"} onCredential={googleSignIn} disabled={busy} />
          {googleClientId() === "" && <p className="setup-note">Google sign-in will activate when the site’s Google client ID is connected.</p>}
          <div className="auth-divider"><span>or continue with email</span></div>

          <form onSubmit={submit} className="auth-form">
            {mode === "register" && (
              <label><span>Name</span><input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required maxLength={80} placeholder="Ricky Kashyap" /></label>
            )}
            <label><span>Email</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required maxLength={254} placeholder="you@example.com" /></label>
            <label><span>Password</span><input type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} required minLength={10} maxLength={128} placeholder="At least 10 characters" /></label>
            {error && <p className="form-error" role="alert">{error}</p>}
            <button className="primary-action auth-submit" type="submit" disabled={busy}>{busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}<ArrowRight aria-hidden="true" /></button>
          </form>

          <p className="auth-switch">
            {mode === "login" ? "New to HourSpeed?" : "Already have an account?"}{" "}
            <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }}>
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </section>
    </main>
  );
}

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header><div><span className="modal-mark"><Sparkles aria-hidden="true" /></span><h2 id="modal-title">{title}</h2></div><button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button></header>
        {children}
      </section>
    </div>
  );
}

function ActivityDialog({
  activity,
  onClose,
  onSave,
}: {
  activity: Activity | null;
  onClose: () => void;
  onSave: (input: {
    name: string;
    category: string | null;
    color: string;
    icon: string;
    targetMinutes: number | null;
    targetPeriod: "day" | "week" | "month" | null;
  }) => Promise<void>;
}) {
  const [name, setName] = useState(activity?.name ?? "");
  const [category, setCategory] = useState(activity?.category ?? "");
  const [color, setColor] = useState(activity?.color ?? "#6c63e8");
  const [icon, setIcon] = useState(activity?.icon ?? "clock");
  const [period, setPeriod] = useState<"none" | "day" | "week" | "month">(activity?.targetPeriod ?? "none");
  const [minutes, setMinutes] = useState(activity?.targetMinutes?.toString() ?? "30");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({
        name,
        category: category.trim() || null,
        color,
        icon,
        targetMinutes: period === "none" ? null : Number(minutes),
        targetPeriod: period === "none" ? null : period,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Activity could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={activity ? "Edit activity" : "Add an activity"} onClose={onClose}>
      <form className="modal-form" onSubmit={submit}>
        <label><span>Activity name</span><input value={name} onChange={(event) => setName(event.target.value)} required maxLength={80} autoFocus placeholder="Japanese" /></label>
        <label><span>Category <small>optional</small></span><input value={category} onChange={(event) => setCategory(event.target.value)} maxLength={80} placeholder="Learning" /></label>
        <div className="form-pair">
          <label><span>Color</span><input className="color-field" type="color" value={color} onChange={(event) => setColor(event.target.value)} /></label>
          <label><span>Icon</span><select value={icon} onChange={(event) => setIcon(event.target.value)}><option value="clock">Clock</option><option value="language">Language</option><option value="dumbbell">Workout</option><option value="book-open">Subject</option></select></label>
        </div>
        <div className="form-pair">
          <label><span>Goal period</span><select value={period} onChange={(event) => setPeriod(event.target.value as typeof period)}><option value="none">No goal yet</option><option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option></select></label>
          <label><span>Target minutes</span><input type="number" min="1" max="43200" value={minutes} disabled={period === "none"} onChange={(event) => setMinutes(event.target.value)} required={period !== "none"} /></label>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <footer className="modal-actions"><button className="secondary-action" type="button" onClick={onClose}>Cancel</button><button className="primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : activity ? "Save changes" : "Add activity"}</button></footer>
      </form>
    </Modal>
  );
}

function SessionDialog({
  session,
  activities,
  onClose,
  onSave,
}: {
  session: Session | null;
  activities: Activity[];
  onClose: () => void;
  onSave: (input: { activityId: string; startedAt: string; endedAt: string; note: string | null }) => Promise<void>;
}) {
  const now = new Date();
  const [activityId, setActivityId] = useState(session?.activityId ?? activities[0]?.id ?? "");
  const [startedAt, setStartedAt] = useState(localInputValue(session ? new Date(session.startedAt) : new Date(now.getTime() - 30 * 60_000)));
  const [endedAt, setEndedAt] = useState(localInputValue(session?.endedAt ? new Date(session.endedAt) : now));
  const [note, setNote] = useState(session?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({
        activityId,
        startedAt: new Date(startedAt).toISOString(),
        endedAt: new Date(endedAt).toISOString(),
        note: note.trim() || null,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Session could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={session ? "Edit session" : "Add time manually"} onClose={onClose}>
      <form className="modal-form" onSubmit={submit}>
        <label><span>Activity</span><select value={activityId} onChange={(event) => setActivityId(event.target.value)} required>{activities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label><span>Started</span><input type="datetime-local" value={startedAt} onChange={(event) => setStartedAt(event.target.value)} required /></label>
        <label><span>Finished</span><input type="datetime-local" value={endedAt} onChange={(event) => setEndedAt(event.target.value)} required /></label>
        <label><span>Note <small>optional</small></span><input value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} placeholder="What did you work on?" /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <footer className="modal-actions"><button className="secondary-action" type="button" onClick={onClose}>Cancel</button><button className="primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : "Save session"}</button></footer>
      </form>
    </Modal>
  );
}

function DashboardApp({ user, onSignedOut }: { user: User; onSignedOut: () => void }) {
  const [view, setView] = useState<View>("today");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [timerSyncedAt, setTimerSyncedAt] = useState(Date.now());
  const [tick, setTick] = useState(Date.now());
  const [activityDialog, setActivityDialog] = useState<Activity | "new" | null>(null);
  const [sessionDialog, setSessionDialog] = useState<Session | "new" | null>(null);
  const [insightPeriod, setInsightPeriod] = useState<InsightPeriod>("7d");
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    (localStorage.getItem("timetable_theme") as "light" | "dark" | null) ??
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"),
  );

  const activeActivities = useMemo(() => activities.filter((item) => item.archivedAt === null), [activities]);
  const archivedActivities = useMemo(() => activities.filter((item) => item.archivedAt !== null), [activities]);

  const handleFailure = useCallback((caught: unknown) => {
    if (caught instanceof ApiError && caught.status === 401) {
      clearToken();
      onSignedOut();
      return;
    }
    setError(caught instanceof Error ? caught.message : "Something went wrong.");
  }, [onSignedOut]);

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [dashboardResult, activityResult, sessionResult] = await Promise.all([
        api.dashboard(),
        api.activities(true),
        api.sessions(20),
      ]);
      setDashboard(dashboardResult);
      setTimerSyncedAt(Date.now());
      setActivities(activityResult.activities);
      setSessions(sessionResult.sessions);
      setSelectedActivityId((current) => current || activityResult.activities.find((item) => item.archivedAt === null)?.id || "");
      setError(null);
    } catch (caught) {
      handleFailure(caught);
    } finally {
      setLoading(false);
    }
  }, [handleFailure]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const interval = window.setInterval(() => setTick(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("timetable_theme", theme);
  }, [theme]);
  useEffect(() => {
    if (notice === null) return;
    const timeout = window.setTimeout(() => setNotice(null), 4_000);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const timer = dashboard?.timer ?? null;
  const timerSeconds = timer
    ? timer.durationSeconds + (timer.status === "running" ? Math.max(0, Math.floor((tick - timerSyncedAt) / 1_000)) : 0)
    : 0;
  const timerSummary = dashboard?.activities.find((item) => item.id === timer?.activityId);

  const runAction = async (action: () => Promise<unknown>, success?: string) => {
    setWorking(true);
    setError(null);
    try {
      await action();
      if (success) setNotice(success);
      await refresh(true);
    } catch (caught) {
      handleFailure(caught);
    } finally {
      setWorking(false);
    }
  };

  const startTimer = (activityId = selectedActivityId) => {
    if (!activityId) {
      setActivityDialog("new");
      return;
    }
    void runAction(() => api.startTimer(activityId), "Timer started");
  };

  const saveActivity = async (input: {
    name: string;
    category: string | null;
    color: string;
    icon: string;
    targetMinutes: number | null;
    targetPeriod: "day" | "week" | "month" | null;
  }) => {
    if (activityDialog && activityDialog !== "new") await api.updateActivity(activityDialog.id, input);
    else await api.createActivity(input);
    setNotice(activityDialog === "new" ? "Activity added" : "Activity updated");
    await refresh(true);
  };

  const saveSession = async (input: { activityId: string; startedAt: string; endedAt: string; note: string | null }) => {
    if (sessionDialog && sessionDialog !== "new") await api.updateSession(sessionDialog.id, input);
    else await api.createSession(input);
    setNotice(sessionDialog === "new" ? "Session added" : "Session updated");
    await refresh(true);
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* Local sign-out still clears an expired token. */ }
    clearToken();
    onSignedOut();
  };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your HourSpeed account and every tracked activity and session? This cannot be undone.")) return;
    setWorking(true);
    setError(null);
    try {
      await api.deleteAccount();
      clearToken();
      onSignedOut();
    } catch (caught) {
      handleFailure(caught);
    } finally {
      setWorking(false);
    }
  };

  const selectedSeries = useMemo(() => {
    if (!dashboard) return [];
    if (insightPeriod === "7d") return dashboard.last30Days.slice(-7);
    return dashboard.last30Days;
  }, [dashboard, insightPeriod]);
  const seriesMax = Math.max(1, ...selectedSeries.map((item) => item.seconds));
  const seriesTotal = selectedSeries.reduce((sum, item) => sum + item.seconds, 0);
  const activeDays = dashboard?.last30Days.filter((item) => item.seconds > 0).length ?? 0;
  const today = dashboard?.totals.today;
  const lastSeven = dashboard?.last30Days.slice(-7) ?? [];
  const lastSevenMax = Math.max(1, ...lastSeven.map((item) => item.seconds));

  if (loading && dashboard === null) return <LoadingScreen />;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="app-brand"><span className="brand-mark"><Timer aria-hidden="true" /></span><span>HourSpeed</span></div>
        <nav aria-label="Primary navigation">
          {NAVIGATION.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} className={view === item.id ? "active" : ""} type="button" onClick={() => setView(item.id)} aria-current={view === item.id ? "page" : undefined}><Icon aria-hidden="true" /><span>{item.label}</span></button>;
          })}
        </nav>
        <div className="sidebar-spacer" />
        {timer ? (
          <div className="sidebar-timer"><span className="live-dot" /><span><small>{timer.status === "running" ? "Tracking" : "Paused"}</small><strong>{timer.activity?.name}</strong><b>{formatClock(timerSeconds)}</b></span></div>
        ) : (
          <div className="sidebar-intention"><Target aria-hidden="true" /><strong>Your time, clearly seen.</strong><span>Every session adds to your lifetime total.</span></div>
        )}
      </aside>

      <main className="app-main">
        <header className="topbar">
          <div><h1>{view === "today" ? `Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, ${user.displayName.split(" ")[0]}` : NAVIGATION.find((item) => item.id === view)?.label}</h1><p>{view === "today" ? new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(new Date()) : view === "insights" ? "Patterns across days and a lifetime" : view === "activities" ? "Everything you want to make time for" : "Preferences and your data"}</p></div>
          <div className="topbar-actions"><button className="icon-button" type="button" onClick={() => setTheme(theme === "light" ? "dark" : "light")} aria-label={`Use ${theme === "light" ? "dark" : "light"} theme`}>{theme === "light" ? <Moon aria-hidden="true" /> : <Sun aria-hidden="true" />}</button><button className="profile-button" type="button" onClick={() => setView("account")}>{user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : <span>{initials(user.displayName)}</span>}<b>{user.displayName}</b><ChevronDown aria-hidden="true" /></button></div>
        </header>

        {error && <div className="app-alert error" role="alert"><span>{error}</span><button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X aria-hidden="true" /></button></div>}
        {notice && <div className="app-alert success" role="status"><Check aria-hidden="true" /><span>{notice}</span></div>}

        {view === "today" && (
          <section className="page-content today-page">
            {activeActivities.length === 0 ? (
              <section className="empty-welcome">
                <span className="empty-mark"><Sparkles aria-hidden="true" /></span><h2>What do you want to make time for?</h2><p>Add your first activity, then start tracking with one click.</p>
                <div className="preset-grid">{ACTIVITY_PRESETS.map((preset) => <button key={preset.name} type="button" onClick={() => void runAction(() => api.createActivity({ ...preset, targetMinutes: preset.name === "Workout" ? 90 : 30, targetPeriod: preset.name === "Workout" ? "week" : "day" }), `${preset.name} added`)}><span style={{ background: preset.color }}>{activityGlyph(preset)}</span><b>{preset.name}</b><small>{preset.category}</small></button>)}</div>
                <button className="secondary-action" type="button" onClick={() => setActivityDialog("new")}><Plus aria-hidden="true" />Create something else</button>
              </section>
            ) : (
              <>
                <section className="timer-hero">
                  <div className="timer-primary">
                    <p className="timer-status"><span className={`live-dot ${timer?.status ?? "idle"}`} />{timer ? timer.status === "running" ? "Session in progress" : "Session paused" : "Ready when you are"}</p>
                    {timer ? <h2>{timer.activity?.name ?? "Current activity"}</h2> : <label className="activity-picker"><span>Start tracking</span><select value={selectedActivityId} onChange={(event) => setSelectedActivityId(event.target.value)}>{activeActivities.map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}</select></label>}
                    <strong className="timer-clock">{formatClock(timerSeconds)}</strong>
                    <div className="timer-actions">
                      {timer ? <button className="timer-main-action" type="button" disabled={working} onClick={() => void runAction(() => timer.status === "running" ? api.pauseTimer(timer.id) : api.resumeTimer(timer.id))}>{timer.status === "running" ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}{timer.status === "running" ? "Pause" : "Resume"}</button> : <button className="timer-main-action" type="button" disabled={working} onClick={() => startTimer()}><Play aria-hidden="true" />Start</button>}
                      {timer && <button className="timer-secondary-action" type="button" disabled={working} onClick={() => void runAction(() => api.stopTimer(timer.id), `${timer.activity?.name ?? "Session"} saved`)}><Square aria-hidden="true" />Finish</button>}
                    </div>
                  </div>
                  <div className="timer-context">
                    {timerSummary ? <><span>Today’s {timerSummary.name}</span><strong>{formatDuration(timerSummary.todaySeconds, true)}</strong>{timerSummary.goal ? <><div className="progress-heading"><span>{timerSummary.goal.period} goal</span><span>{Math.min(999, timerSummary.goal.percent)}%</span></div><div className="progress-track" role="progressbar" aria-label={`${timerSummary.name} goal`} aria-valuenow={timerSummary.goal.percent} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${Math.min(100, timerSummary.goal.percent)}%` }} /></div></> : <p>No goal set yet. Your lifetime total still grows.</p>}</> : <><span>Today across everything</span><strong>{formatDuration(today?.currentSeconds ?? 0, true)}</strong><p><TrendingUp aria-hidden="true" />{today ? formatDelta(today.changeSeconds) : "Your first session starts the story"}</p></>}
                  </div>
                </section>

                <section className="metric-grid" aria-label="Progress summary">
                  <article><span>Time invested today</span><strong>{formatDuration(today?.currentSeconds ?? 0, true)}</strong><small className={(today?.changeSeconds ?? 0) >= 0 ? "positive" : "neutral"}><ArrowUpRight aria-hidden="true" />{today ? `${formatDelta(today.changeSeconds)} than yesterday` : "No comparison yet"}</small></article>
                  <article><span>Active days (30d)</span><strong>{activeDays} days</strong><small className="positive"><Flame aria-hidden="true" />Progress survives missed days</small></article>
                  <article><span>Lifetime investment</span><strong>{formatDuration(dashboard?.totals.lifetime.seconds ?? 0, true)}</strong><small className="positive"><Target aria-hidden="true" />{dashboard?.totals.lifetime.sessions ?? 0} completed sessions</small></article>
                </section>

                <div className="dashboard-grid">
                  <section className="content-panel chart-panel">
                    <header><div><h2>Your week</h2><p>{formatDuration(lastSeven.reduce((sum, item) => sum + item.seconds, 0), true)} across all activities</p></div><button className="text-action" type="button" onClick={() => setView("insights")}>Details <ArrowUpRight aria-hidden="true" /></button></header>
                    <div className="bar-chart" role="img" aria-label="Time invested over the last seven days">{lastSeven.map((item) => <div className="bar-column" key={item.date}><span className="bar-value" style={{ height: `${Math.max(4, (item.seconds / lastSevenMax) * 100)}%` }} title={`${formatLocalDate(item.date)}: ${formatDuration(item.seconds, true)}`} /><small>{new Intl.DateTimeFormat(undefined, { weekday: "narrow" }).format(new Date(`${item.date}T12:00:00`))}</small></div>)}</div>
                  </section>
                  <section className="content-panel breakdown-panel"><header><div><h2>Today by activity</h2><p>{dashboard?.activities.filter((item) => item.todaySeconds > 0).length ?? 0} priorities touched</p></div></header><div className="breakdown-list">{dashboard?.activities.filter((item) => item.todaySeconds > 0).map((item) => <div key={item.id}><span className="activity-symbol" style={{ color: item.color, backgroundColor: `${item.color}1f` }}>{activityGlyph(item)}</span><span><b>{item.name}</b><small>{item.lifetimeSessions} lifetime sessions</small></span><strong>{formatDuration(item.todaySeconds, true)}</strong></div>)}{dashboard?.activities.every((item) => item.todaySeconds === 0) && <p className="empty-copy">Start a timer and today’s breakdown will appear here.</p>}</div></section>
                </div>

                <section className="content-panel recent-panel"><header><div><h2>Recent sessions</h2><p>A trustworthy record you can correct</p></div><button className="secondary-action" type="button" onClick={() => setSessionDialog("new")}><Plus aria-hidden="true" />Add time</button></header><div className="session-list">{sessions.slice(0, 6).map((session) => <article key={session.id}><span className="activity-symbol" style={{ color: session.activity?.color ?? "#6c63e8", backgroundColor: `${session.activity?.color ?? "#6c63e8"}1f` }}>{activityGlyph({ icon: session.activity?.icon, name: session.activity?.name ?? "Activity" })}</span><span><b>{session.activity?.name ?? "Activity"}</b><small>{formatSessionTime(session)}</small></span><strong>{formatDuration(session.durationSeconds, true)}</strong><div className="row-actions"><button type="button" onClick={() => setSessionDialog(session)} aria-label={`Edit ${session.activity?.name ?? "session"}`}><Edit3 aria-hidden="true" /></button><button type="button" onClick={() => { if (window.confirm("Delete this completed session? Its time will be removed from your totals.")) void runAction(() => api.deleteSession(session.id), "Session deleted"); }} aria-label={`Delete ${session.activity?.name ?? "session"}`}><Trash2 aria-hidden="true" /></button></div></article>)}{sessions.length === 0 && <p className="empty-copy">Completed sessions will appear here.</p>}</div></section>
              </>
            )}
          </section>
        )}

        {view === "insights" && (
          <section className="page-content">
            <div className="page-title-row"><div><h2>Understand your investment</h2><p>Exact time and grounded comparisons—never a made-up productivity score.</p></div><div className="period-tabs" aria-label="Insight period">{(["7d", "30d", "lifetime"] as const).map((period) => <button key={period} className={insightPeriod === period ? "active" : ""} type="button" onClick={() => setInsightPeriod(period)} aria-pressed={insightPeriod === period}>{period === "7d" ? "7 days" : period === "30d" ? "30 days" : "Lifetime"}</button>)}</div></div>
            <section className="lifetime-banner"><div><span>{insightPeriod === "lifetime" ? "Invested since your first session" : `Invested in the last ${insightPeriod === "7d" ? "7" : "30"} days`}</span><strong>{formatDuration(insightPeriod === "lifetime" ? dashboard?.totals.lifetime.seconds ?? 0 : seriesTotal, true)}</strong><p><TrendingUp aria-hidden="true" />{insightPeriod === "7d" ? formatDelta(dashboard?.totals.week.changeSeconds ?? 0) + " than the previous week" : insightPeriod === "30d" ? formatDelta(dashboard?.totals.month.changeSeconds ?? 0) + " than the previous month" : `${dashboard?.totals.lifetime.sessions ?? 0} sessions across your life`}</p></div><span className="milestone"><Sparkles aria-hidden="true" /><b>{Math.floor((dashboard?.totals.lifetime.seconds ?? 0) / 3_600)}h</b><small>lifetime</small></span></section>
            <div className="insights-grid"><section className="content-panel chart-panel"><header><div><h2>Time trend</h2><p>{insightPeriod === "lifetime" ? "Your recent 30 days remain visible for context" : "Daily time across the selected period"}</p></div></header><div className={`bar-chart insights-chart ${selectedSeries.length > 10 ? "dense" : ""}`} role="img" aria-label={`Time trend for ${insightPeriod}`}>{selectedSeries.map((item, index) => <div className="bar-column" key={item.date}><span className="bar-value" style={{ height: `${Math.max(3, (item.seconds / seriesMax) * 100)}%` }} title={`${formatLocalDate(item.date)}: ${formatDuration(item.seconds, true)}`} /><small>{selectedSeries.length <= 7 || index % 5 === 0 ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(`${item.date}T12:00:00`)) : ""}</small></div>)}</div></section><section className="content-panel allocation-panel"><header><div><h2>Time allocation</h2><p>Where your effort is going</p></div></header><div>{dashboard?.activities.slice().sort((a, b) => b.lifetimeSeconds - a.lifetimeSeconds).slice(0, 6).map((item) => { const amount = insightPeriod === "7d" ? item.weekSeconds : insightPeriod === "30d" ? item.monthSeconds : item.lifetimeSeconds; const total = dashboard.activities.reduce((sum, activity) => sum + (insightPeriod === "7d" ? activity.weekSeconds : insightPeriod === "30d" ? activity.monthSeconds : activity.lifetimeSeconds), 0); const percent = total === 0 ? 0 : Math.round((amount / total) * 100); return <article key={item.id}><div><b>{item.name}</b><span>{formatDuration(amount, true)} · {percent}%</span></div><div className="allocation-track"><span style={{ width: `${percent}%`, background: item.color }} /></div></article>; })}</div></section></div>
            <section className="content-panel consistency-panel"><header><div><h2>Consistency</h2><p>{activeDays} active days in the last 30 days; empty days remain visible without erasing progress.</p></div></header><div className="heatmap" aria-label={`${activeDays} active days in the last 30 days`}>{dashboard?.last30Days.map((item) => { const level = Math.min(4, Math.ceil((item.seconds / Math.max(1, ...dashboard.last30Days.map((day) => day.seconds))) * 4)); return <span key={item.date} data-level={level} title={`${formatLocalDate(item.date)}: ${formatDuration(item.seconds, true)}`} />; })}</div></section>
          </section>
        )}

        {view === "activities" && (
          <section className="page-content">
            <div className="page-title-row"><div><h2>Your priorities</h2><p>Add, edit, archive, restore, or start anything in one place.</p></div><button className="primary-action" type="button" onClick={() => setActivityDialog("new")}><Plus aria-hidden="true" />Add activity</button></div>
            <div className="activity-list">{activeActivities.map((activity) => { const summary = dashboard?.activities.find((item) => item.id === activity.id); return <article key={activity.id} className="activity-row"><span className="activity-symbol large" style={{ color: activity.color, backgroundColor: `${activity.color}1f` }}>{activityGlyph(activity)}</span><div className="activity-copy"><h3>{activity.name}</h3><p>{activity.category ?? "Personal"} · {formatDuration(summary?.lifetimeSeconds ?? 0, true)} lifetime</p></div><div className="activity-goal">{summary?.goal ? <><div><span>{formatDuration(summary.goal.currentSeconds, true)} of {formatDuration(summary.goal.targetMinutes * 60, true)} {summary.goal.period}</span><b>{Math.min(999, summary.goal.percent)}%</b></div><div className="progress-track"><span style={{ width: `${Math.min(100, summary.goal.percent)}%`, background: activity.color }} /></div></> : <span>No goal—tracking still counts</span>}</div><div className="activity-buttons"><button className="quick-start" type="button" disabled={working || timer !== null} onClick={() => startTimer(activity.id)}><Play aria-hidden="true" />Start</button><button className="icon-button" type="button" onClick={() => setActivityDialog(activity)} aria-label={`Edit ${activity.name}`}><Edit3 aria-hidden="true" /></button><button className="icon-button" type="button" onClick={() => { if (window.confirm(`Archive ${activity.name}? Existing sessions and lifetime totals will stay intact.`)) void runAction(() => api.archiveActivity(activity.id), `${activity.name} archived`); }} aria-label={`Archive ${activity.name}`}><Archive aria-hidden="true" /></button></div></article>; })}{activeActivities.length === 0 && <p className="empty-copy">No active activities yet.</p>}</div>
            {archivedActivities.length > 0 && <section className="archived-section"><h2>Archived</h2><div className="activity-list compact">{archivedActivities.map((activity) => <article key={activity.id} className="activity-row"><span className="activity-symbol" style={{ color: activity.color, backgroundColor: `${activity.color}1f` }}>{activityGlyph(activity)}</span><div className="activity-copy"><h3>{activity.name}</h3><p>History and totals preserved</p></div><div /><button className="secondary-action" type="button" onClick={() => void runAction(() => api.restoreActivity(activity.id), `${activity.name} restored`)}><RotateCcw aria-hidden="true" />Restore</button></article>)}</div></section>}
          </section>
        )}

        {view === "account" && (
          <section className="page-content account-page"><div className="account-hero">{user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : <span>{initials(user.displayName)}</span>}<div><h2>{user.displayName}</h2><p>{user.email}</p></div></div><section className="content-panel settings-panel"><header><div><h2>Profile and preferences</h2><p>Your reporting day follows your saved timezone.</p></div></header><div className="setting-row"><span className="setting-icon"><Clock3 aria-hidden="true" /></span><span><b>Timezone</b><small>{user.timezone}</small></span></div><div className="setting-row"><span className="setting-icon"><Moon aria-hidden="true" /></span><span><b>Appearance</b><small>{theme === "light" ? "Light theme" : "Dark theme"}</small></span><button className="secondary-action" type="button" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>Switch</button></div><div className="setting-row"><span className="setting-icon"><LogOut aria-hidden="true" /></span><span><b>Sign out</b><small>Your tracked data stays safely in your account.</small></span><button className="secondary-action danger" type="button" onClick={() => void logout()}>Sign out</button></div><div className="setting-row"><span className="setting-icon"><Trash2 aria-hidden="true" /></span><span><b>Delete account</b><small>Permanently removes your profile, activities, sessions, and lifetime totals.</small></span><button className="secondary-action danger" type="button" disabled={working} onClick={() => void deleteAccount()}>Delete account</button></div></section></section>
        )}

        {timer && view !== "today" && <div className="mobile-live-strip"><span className="live-dot" /><span><b>{timer.activity?.name}</b><small>{formatClock(timerSeconds)}</small></span><button type="button" onClick={() => void runAction(() => timer.status === "running" ? api.pauseTimer(timer.id) : api.resumeTimer(timer.id))} aria-label={timer.status === "running" ? "Pause timer" : "Resume timer"}>{timer.status === "running" ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}</button></div>}
        <nav className="mobile-nav" aria-label="Mobile navigation">{NAVIGATION.map((item) => { const Icon = item.icon; return <button key={item.id} className={view === item.id ? "active" : ""} type="button" onClick={() => setView(item.id)} aria-current={view === item.id ? "page" : undefined}><Icon aria-hidden="true" /><span>{item.label}</span></button>; })}</nav>
      </main>

      {activityDialog && <ActivityDialog activity={activityDialog === "new" ? null : activityDialog} onClose={() => setActivityDialog(null)} onSave={saveActivity} />}
      {sessionDialog && <SessionDialog session={sessionDialog === "new" ? null : sessionDialog} activities={activeActivities} onClose={() => setSessionDialog(null)} onSave={saveSession} />}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(getStoredToken() !== null);

  useEffect(() => {
    if (getStoredToken() === null) {
      setBooting(false);
      return;
    }
    api.me()
      .then((result) => setUser(result.user))
      .catch(() => clearToken())
      .finally(() => setBooting(false));
  }, []);

  if (booting) return <LoadingScreen />;
  if (user === null) return <AuthScreen onAuthenticated={(result) => setUser(result.user)} />;
  return <DashboardApp user={user} onSignedOut={() => setUser(null)} />;
}
